import {
    HasPercentageNumber,
    HasPercentageString,
    LegalEntityType,
    MemberOwnership,
    MemberOwnershipFormData,
    MemberOwnershipWriteModel,
    OwnershipCategory,
    OwnershipDetailsFormData,
    OwnershipReadModel,
    OwnershipWriteModel,
    SoleOwnerType,
} from "../models/Ownership";
import {InvestorGroupMember, MemberGroup, MemberGroupMember} from "../../ClientManagement/models/InvestorGroupType";
import {calculateBirthdateFromAge} from "../../utils/dateUtils";

export function mapToOwnershipWriteModel(formData: OwnershipDetailsFormData): OwnershipWriteModel {
    const nonClientMemberWriteModels: MemberOwnershipWriteModel[] = formData.nonClientMemberOwnerships
        .map(mapPercentageToFloat);
    const memberOwnershipWriteModels: MemberOwnershipWriteModel[] = formData.memberOwnerships
        .map(mapPercentageToFloat);
    memberOwnershipWriteModels.push(...nonClientMemberWriteModels);

    return {
        ownershipCategory: formData.ownershipCategory,
        memberOwnerships: memberOwnershipWriteModels,
        legalEntityOwnerships: formData.legalEntityOwnerships.map(ownership => {
            return {
                legalEntity: {
                    type: ownership.type!, name: ownership.name,
                },
                showWithinOutOfEstateReport: ownership.showWithinOutOfEstateReport,
                percentage: mapPercentageToFloat(ownership).percentage
            }
        })
    };
}

export function mapToOwnershipDetailsFormData(ownershipReadModel: OwnershipReadModel): OwnershipDetailsFormData {
    return {
        soleOwnerType: getSoleOwnerType(ownershipReadModel),
        ownershipCategory: ownershipReadModel.ownershipCategory,
        memberOwnerships: ownershipReadModel.memberOwnerships.filter(isClientMember).map(mapToMemberOwnershipFormData),
        nonClientMemberOwnerships: ownershipReadModel.memberOwnerships.filter(o => !isClientMember(o)).map(mapToMemberOwnershipFormData),
        legalEntityOwnerships: ownershipReadModel.legalEntityOwnerships.map(readModel => {
            return {
                legalEntityId: readModel.legalEntity.id,
                name: readModel.legalEntity.name,
                type: readModel.legalEntity.type,
                percentage: mapPercentageToString(readModel).percentage,
                showWithinOutOfEstateReport: readModel.showWithinOutOfEstateReport
            }
        }),
    };
}

export function extractOwnershipDetailsFormData(formData: OwnershipDetailsFormData): OwnershipDetailsFormData {
    return {
        soleOwnerType: formData.soleOwnerType,
        ownershipCategory: formData.ownershipCategory,
        memberOwnerships: formData.memberOwnerships,
        nonClientMemberOwnerships: formData.nonClientMemberOwnerships,
        legalEntityOwnerships: formData.legalEntityOwnerships,
    };
}

export function mapSoleMemberOwnershipFormData(ownerMemberId: string): MemberOwnershipFormData {
    return {
        memberId: ownerMemberId,
        percentage: "100.00",
        isRevocableTrust: false
    };
}

export function mapSoleMemberOwnershipWriteModel(ownerMemberId: string): MemberOwnershipWriteModel {
    return {
        memberId: ownerMemberId,
        percentage: 100,
        isRevocableTrust: false
    };
}

export function mapSoleMemberOwnership(ownerMemberId: string): MemberOwnership {
    return {
        member: {
            id: ownerMemberId,
            name: '',
        },
        percentage: 100,
        isRevocableTrust: false,
        isInEstateMember: true,
    };
}

export function doesCategoryAllowNonClientOwnerships(category: OwnershipCategory): boolean {
    return ["TIC (Tenants in Common)", "Partial"].includes(category);
}

export function mapMemberOwnershipToWriteModel(memberOwnership: MemberOwnership): MemberOwnershipWriteModel {
    return {
        memberId: memberOwnership.member.id,
        percentage: memberOwnership.percentage,
        isRevocableTrust: memberOwnership.isRevocableTrust
    };
}

export function mapToMemberOwnershipWriteModel(memberOwnership: MemberOwnershipFormData): MemberOwnershipWriteModel {
    return {
        memberId: memberOwnership.memberId,
        percentage: mapPercentageToFloat(memberOwnership).percentage,
        isRevocableTrust: memberOwnership.isRevocableTrust,
    };
}

export function mapToMemberOwnershipFormData(memberOwnership: MemberOwnership): MemberOwnershipFormData {
    return {
        memberId: memberOwnership.member.id,
        percentage: mapPercentageToString(memberOwnership).percentage,
        isRevocableTrust: memberOwnership.isRevocableTrust,
    };
}

export function mapToInvestorMemberGroupMember(member: MemberGroupMember | undefined): InvestorGroupMember | undefined {
    if (member !== null && member !== undefined) {
        return {
            id: member.id,
            name: member.firstName + " " + member.lastName,
            firstName: member.firstName,
            birthdate: calculateBirthdateFromAge(member.age),
        }
    } else {
        return undefined;
    }
}

export const mapOwnershipDetails = (ownershipDetailsFormData: any) => {
    return {
        ownershipCategory: ownershipDetailsFormData.ownershipCategory,
        soleOwnerType: getSoleOwnerType(ownershipDetailsFormData),
        legalEntityOwnerships: (ownershipDetailsFormData.legalEntityOwnerships || []).map((ownership: {
            legalEntityId: string;
            name: string;
            type: LegalEntityType;
            percentage: number;
            showWithinOutOfEstateReport: boolean;
        }) => ({
            legalEntityId: ownership.legalEntityId || '',
            name: ownership.name || '',
            type: ownership.type,
            percentage: ownership.percentage.toString(),
            showWithinOutOfEstateReport: ownership.showWithinOutOfEstateReport
        })),
        nonClientMemberOwnerships: (ownershipDetailsFormData.nonClientMemberOwnerships || [])
            .map((ownership: { memberId: string; percentage: number; isRevocableTrust: boolean }) => ({
                memberId: ownership.memberId || '',
                percentage: ownership.percentage.toString(),
                isRevocableTrust: ownership.isRevocableTrust,
            })),
        memberOwnerships: (ownershipDetailsFormData.memberOwnerships || [])
            .map((ownership: { memberId: string; percentage: number; isRevocableTrust: boolean; }) => ({
                memberId: ownership.memberId || '',
                percentage: ownership.percentage.toString(),
                isRevocableTrust: ownership.isRevocableTrust,
            })),
    };
};
export const mapOwnershipDetailsByAsset = (asset: any) => {
    let soleOwnerType;
    if (asset.ownershipCategory === "Sole") {
        if (asset.legalEntityOwnerships.length > 0) {
            soleOwnerType = asset.legalEntityOwnerships[0].legalEntity.type;
        } else {
            soleOwnerType = "Individual";
        }
    } else {
        soleOwnerType = undefined;
    }
    return {
        ownershipCategory: asset.ownershipCategory,
        soleOwnerType,
        legalEntityOwnerships: asset.legalEntityOwnerships.map((ownership: {
            legalEntity: { name: string, type: LegalEntityType, id: string };
            percentage: number;
            showWithinOutOfEstateReport: boolean;
        }) => ({
            legalEntityId: ownership.legalEntity.id || '',
            name: ownership.legalEntity.name || '',
            type: ownership.legalEntity.type,
            percentage: ownership.percentage.toString(),
            showWithinOutOfEstateReport: ownership.showWithinOutOfEstateReport
        })),
        nonClientMemberOwnerships: asset.memberOwnerships
            .filter((ownership: { isInEstateMember: boolean }) => !ownership.isInEstateMember)
            .map((ownership: { member: { id: string }; percentage: number; isRevocableTrust: boolean }) => ({
                memberId: ownership.member.id || '',
                percentage: ownership.percentage.toString(),
                isRevocableTrust: ownership.isRevocableTrust,
            })),
        memberOwnerships: asset.memberOwnerships
            .filter((ownership: { isInEstateMember: boolean }) => ownership.isInEstateMember)
            .map((ownership: { member: { id: string }; percentage: number; isRevocableTrust: boolean; }) => ({
                memberId: ownership.member.id || '',
                percentage: ownership.percentage.toString(),
                isRevocableTrust: ownership.isRevocableTrust,
            })),
    };
};

function isClientMember(memberOwnership: MemberOwnership): boolean {
    return memberOwnership.isInEstateMember;
}

export function mapToOwnershipReadModel(formData: OwnershipDetailsFormData,
                                        memberGroup?: MemberGroup,
                                        previousOwnership?: OwnershipReadModel): OwnershipReadModel {
    const memberGroupMembers = memberGroup
        ? [memberGroup.primaryMember, memberGroup.partnerMember, ...memberGroup.additionalMembers]
        : [];
    const getMemberName = (memberId: string) => {
        const memberGroupMember = memberGroupMembers.find(member => member?.id === memberId);
        if (memberGroupMember) {
            return `${memberGroupMember.firstName} ${memberGroupMember.lastName}`;
        }
        return previousOwnership?.memberOwnerships
            .find(ownership => ownership.member.id === memberId)?.member.name ?? '';
    };

    const mapMemberOwnership = (ownership: MemberOwnershipFormData, isInEstateMember: boolean): MemberOwnership => ({
        member: {id: ownership.memberId, name: getMemberName(ownership.memberId)},
        percentage: parseFloat(ownership.percentage),
        isRevocableTrust: ownership.isRevocableTrust,
        isInEstateMember,
    });

    return {
        ownershipCategory: formData.ownershipCategory,
        memberOwnerships: [
            ...formData.memberOwnerships.map(ownership => mapMemberOwnership(ownership, true)),
            ...formData.nonClientMemberOwnerships.map(ownership => mapMemberOwnership(ownership, false)),
        ],
        legalEntityOwnerships: formData.legalEntityOwnerships.map(ownership => ({
            legalEntity: {
                id: ownership.legalEntityId ?? '',
                name: ownership.name,
                type: ownership.type!,
            },
            percentage: parseFloat(ownership.percentage),
            showWithinOutOfEstateReport: ownership.showWithinOutOfEstateReport,
        })),
    };
}

function mapPercentageToFloat<T extends HasPercentageString>(ownership: T): Omit<T, 'percentage'> & HasPercentageNumber {
    return {
        ...ownership, percentage: parseFloat(ownership.percentage)
    };
}

function mapPercentageToString<T extends HasPercentageNumber>(ownership: T): Omit<T, 'percentage'> & HasPercentageString {
    return {
        ...ownership, percentage: ownership.percentage.toString()
    };
}

export function getSoleOwnerType(ownershipDetails: OwnershipReadModel): SoleOwnerType | undefined {
    let soleOwnerType: SoleOwnerType | undefined;
    if (ownershipDetails.ownershipCategory === "Sole") {
        if (ownershipDetails.legalEntityOwnerships && ownershipDetails.legalEntityOwnerships.length > 0) {
            soleOwnerType = ownershipDetails.legalEntityOwnerships[0].legalEntity.type;
        } else {
            soleOwnerType = "Individual";
        }
        return soleOwnerType;
    } else {
        soleOwnerType = undefined;
        return soleOwnerType;
    }
}
